import React from "react";
import * as d3 from "d3";
import { ScaleLinear } from "d3";

import { Bounds2D } from "../LineChartUtils";
import { TickInterval } from "./LineChart";
import { useTheme } from "@mui/material";

interface YAxisProps {
  bounds: Bounds2D;
  fontSize: number;
  scale: ScaleLinear<number, number>;
  tickFormatter?(value: number, index: number, domain: Array<number>): string;
  tickInterval?: TickInterval;
}

type AlignmentBaseline = "auto" | "baseline" | "before-edge" | "text-before-edge" 
  | "middle" | "central" | "after-edge" | "text-after-edge" | "ideographic" 
  | "alphabetic" | "hanging" | "mathematical" | "inherit" | undefined;

export default function YAxis(props: YAxisProps): React.JSX.Element {
  const theme = useTheme();
  const { height, width, x, y } = props.bounds;
  const tickLength = props.fontSize / 2;

  const tickCountMax = height / (4 * props.fontSize);
  const domain = props.scale.domain();
  let ticks: Array<number> = [];
  if (tickCountMax <= 0.5) {
    // Only show the maximum domain value (the y-axis order is inverted).
    ticks = [domain[0]];
  } else if (tickCountMax <= 2 || props.tickInterval === "firstLast") {
    // Only show the minimum and maximum domain value.
    ticks = domain;
  } else if (tickCountMax > 2 && props.tickInterval === "interval") {
    // Create a number of ticks between the minimum and maximum domain value.
    const tickCount = Math.round(tickCountMax);
    if (domain[0] < tickCountMax) {
      ticks = d3.quantize(d3.interpolate(domain[0], domain[1]), tickCount);
    } else {
      ticks = d3.quantize(d3.interpolateRound(domain[0], domain[1]), tickCount);
    }
  }

  return (
    <g
      name="y-axis"
      transform={`translate(${x}, ${y})`}
      height={height}
      width={width}
    >
      <path
        d={["M", width, 0, "L", width, height].join(" ")}
        fill="none"
        stroke={theme.palette.text.secondary}
        strokeWidth={1}
      />

      <g name="ticks">
        {ticks.map((value, index, arr) => {
          const yPos = props.scale(value);

          let baseline: AlignmentBaseline = "middle";
          if (index === 0) {
            baseline = "hanging";
          }

          let tickText = `${value}`;
          if (props.tickFormatter) {
            tickText = props.tickFormatter(value, index, props.scale.domain());
          }

          return (
            <g
              key={`tick-${index}`}
              name={`tick-${value}`}
              transform={`translate(${width}, 0)`}
            >
              <line
                x2={-tickLength}
                y1={yPos}
                y2={yPos}
                stroke={theme.palette.text.secondary}
                strokeWidth={1}
              />
              <text
                x={-tickLength*1.5}
                y={yPos}
                alignmentBaseline={baseline}
                style={{
                  fill: theme.palette.text.secondary,
                  fontSize: `${props.fontSize}px`,
                  textAnchor: "end"
                }}
              >
                {tickText}
              </text>
            </g>
          )
        })}
      </g>
    </g>
  );
}